import {
  type ArtefactKind,
  type AssessmentState,
  AssessmentStateSchema,
  type AssessmentSummary,
  AssessmentSummarySchema,
  type Domain,
  DomainSchema,
  type Next,
  NextSchema,
  type SourceArtefact,
  SourceArtefactSchema,
  type TransitionRejection,
  TransitionRejectionSchema,
} from '@zevik/schemas';
import { detailOf, identityHeaders, ReadFailedError, type Source } from './client';

/**
 * Everything the console reads and does between signing in and the review
 * gate (docs/DECISIONS.md ZD-124).
 *
 * The workspace's reads are in `client.ts`; these are the landing page's and
 * the assessment page's — list, create, upload, arm, confirm — and they keep
 * the same two rules. **A refusal is the API's sentence**: `ActRefusedError`
 * carries the status and the `{error}` string and this module composes
 * nothing over it, because the routes were written to answer one sentence
 * per refusal and a page that reworded them would be a lookup table over a
 * decision the API took (ZD-118 §3). **A read is parsed with the schema the
 * route validates against**, so a route answering the right shape with the
 * wrong content fails here rather than rendering.
 *
 * ## `next` is parsed with `NextSchema`, and nothing is read off the state
 *
 * The snapshot carries `header.state` and this module parses it — for the
 * schema check, since no screen renders it (ZD-124 §9) — and every decision
 * the page makes is made on `next` (`pipeline/screen.ts`). A snapshot whose `next` does not parse is
 * refused as a read failure naming `next`, because a page that fell back to
 * the state name would be carrying the transition table at exactly the
 * moment the protocol had changed under it.
 *
 * ## `advance` returns its 409s rather than throwing them
 *
 * `POST /advance` answers 202 armed, 409 `already_running` and 409
 * `nothing_to_run`, each with `next` in the body. The two 409s are not
 * refusals a person did anything about — a double press, a press at a halt —
 * and both carry the answer the page wants, so they are returned as outcomes
 * with `next` and the page adopts it. Any other status is a refusal.
 *
 * ## `approve` returns the gate's refusal as an outcome too (ZD-125 §3)
 *
 * `POST /approve` answers 200 through the gate or 409 with the transition's
 * rejection — the guard's sentence and, where the refusal is about findings,
 * their ids as a field. That refusal is the screen's whole content: which
 * findings still need the reviewer, said by title (`review/gate.ts`). It is
 * parsed with `TransitionRejectionSchema`, the schema the orchestrator's
 * type is inferred from, for `next`'s reason. A 409 that does not parse is a
 * read failure naming the protocol, not a refusal to show.
 */

export class ActRefusedError extends Error {
  constructor(
    readonly what: string,
    readonly status: number,
    /** The API's own sentence, verbatim. */
    readonly detail: string,
  ) {
    super(`${what}: the API answered ${String(status)}. ${detail}`);
    this.name = 'ActRefusedError';
  }
}

export interface Project {
  project_id: string;
  name: string;
}

export interface AssessmentSnapshot {
  assessment_id: string;
  state: AssessmentState;
  /** The domains this assessment may be analysed for — frozen at creation from the tenant's set. */
  enabled_domains: Domain[];
  next: Next;
}

const base = (source: Source): string => {
  if (source.base === null) {
    // Reads of the fixture never reach here: the page routes `?fixture` to the
    // review screen directly. Thrown rather than defaulted so a new caller
    // finds out.
    throw new ReadFailedError('a live route', 0, 'The fixture has no API to call.');
  }
  return source.base.replace(/\/+$/u, '');
};

async function get(source: Source, path: string, what: string): Promise<unknown> {
  const response = await fetch(`${base(source)}${path}`, {
    headers: identityHeaders(source),
    credentials: 'same-origin',
  });
  if (!response.ok) throw new ReadFailedError(what, response.status, await detailOf(response));
  return response.json();
}

async function post(
  source: Source,
  path: string,
  body: BodyInit,
  contentType: string | null,
): Promise<Response> {
  return fetch(`${base(source)}${path}`, {
    method: 'POST',
    headers: {
      ...identityHeaders(source),
      ...(contentType === null ? {} : { 'content-type': contentType }),
    },
    body,
    credentials: 'same-origin',
  });
}

const json = (value: unknown): BodyInit => JSON.stringify(value);

// ---------------------------------------------------------------------------
// The landing page
// ---------------------------------------------------------------------------

/** `GET /v1/assessments`: every assessment the tenant has, newest first, in the route's order. */
export async function listAssessments(source: Source): Promise<AssessmentSummary[]> {
  const payload = (await get(source, '/v1/assessments', 'the assessments')) as {
    assessments?: unknown;
  };
  if (!Array.isArray(payload.assessments)) {
    throw new ReadFailedError('the assessments', 200, 'The response carried no assessments array.');
  }
  return payload.assessments.map((row) => AssessmentSummarySchema.parse(row));
}

export async function listProjects(source: Source): Promise<Project[]> {
  const payload = (await get(source, '/v1/projects', 'the projects')) as { projects?: unknown };
  if (!Array.isArray(payload.projects)) {
    throw new ReadFailedError('the projects', 200, 'The response carried no projects array.');
  }
  return payload.projects.map((row, index) => {
    const project = row as { project_id?: unknown; name?: unknown };
    if (typeof project.project_id !== 'string' || typeof project.name !== 'string') {
      throw new ReadFailedError(
        'the projects',
        200,
        `Project ${String(index)} carried no id or name.`,
      );
    }
    return { project_id: project.project_id, name: project.name };
  });
}

export async function createProject(source: Source, name: string): Promise<Project> {
  const response = await post(source, '/v1/projects', json({ name }), 'application/json');
  if (response.status !== 201) {
    throw new ActRefusedError(
      'the project was not created',
      response.status,
      await detailOf(response),
    );
  }
  const body = (await response.json()) as { project: { project_id: string; name: string } };
  return { project_id: body.project.project_id, name: body.project.name };
}

/** `POST /v1/assessments`: the 201 body is the orchestrator's snapshot, of which the id is what a page needs. */
export async function createAssessment(
  source: Source,
  input: { project_id: string; title: string },
): Promise<{ assessment_id: string }> {
  const response = await post(source, '/v1/assessments', json(input), 'application/json');
  if (response.status !== 201) {
    throw new ActRefusedError(
      'the assessment was not created',
      response.status,
      await detailOf(response),
    );
  }
  const body = (await response.json()) as { header?: { assessment_id?: unknown } };
  if (typeof body.header?.assessment_id !== 'string') {
    throw new ReadFailedError('the new assessment', 201, 'The response carried no header.');
  }
  return { assessment_id: body.header.assessment_id };
}

// ---------------------------------------------------------------------------
// The assessment page
// ---------------------------------------------------------------------------

export function parseSnapshot(payload: unknown): AssessmentSnapshot {
  const body = payload as {
    header?: { assessment_id?: unknown; state?: unknown };
    facts?: { enabled_domains?: unknown };
    next?: unknown;
  };
  if (typeof body.header?.assessment_id !== 'string') {
    throw new ReadFailedError('the assessment', 200, 'The response carried no header.');
  }
  const next = NextSchema.safeParse(body.next);
  if (!next.success) {
    throw new ReadFailedError(
      'the assessment',
      200,
      'The response carried no `next` this console can read, so it cannot say what the ' +
        'assessment is waiting on. This is a deployment defect rather than something you did.',
    );
  }
  // Refused rather than defaulted to `[]`: an empty list here would put a
  // domain screen with nothing to confirm in front of a person, which is an
  // absence dressed as a fact (ZD-036).
  const domains = body.facts?.enabled_domains;
  if (!Array.isArray(domains)) {
    throw new ReadFailedError('the assessment', 200, 'The response carried no enabled domains.');
  }
  return {
    assessment_id: body.header.assessment_id,
    state: AssessmentStateSchema.parse(body.header.state),
    enabled_domains: domains.map((domain) => DomainSchema.parse(domain)),
    next: next.data,
  };
}

export async function readAssessment(source: Source, id: string): Promise<AssessmentSnapshot> {
  return parseSnapshot(await get(source, `/v1/assessments/${id}`, 'the assessment'));
}

export async function listArtefacts(source: Source, id: string): Promise<SourceArtefact[]> {
  const payload = (await get(source, `/v1/assessments/${id}/artefacts`, 'the documents')) as {
    artefacts?: unknown;
  };
  if (!Array.isArray(payload.artefacts)) {
    throw new ReadFailedError('the documents', 200, 'The response carried no artefacts array.');
  }
  return payload.artefacts.map((row) => SourceArtefactSchema.parse(row));
}

/**
 * `POST /:id/artefacts` with the `FormData` body the route reads: `file`,
 * `kind` and `media_type`. The media type is sent explicitly because the
 * browser's own may be empty (`upload/kind.ts`), and the route reads the
 * part before the file's type. 201 stored and recorded; 202 stored and not
 * recorded on the assessment, which the route reports as a warning and this
 * carries through rather than folding into success.
 */
export async function uploadArtefact(
  source: Source,
  id: string,
  input: { file: File; kind: ArtefactKind; media_type: string },
): Promise<{ artefact: SourceArtefact; warning: string | null }> {
  const form = new FormData();
  form.set('kind', input.kind);
  form.set('media_type', input.media_type);
  form.set('file', input.file, input.file.name);
  const response = await post(source, `/v1/assessments/${id}/artefacts`, form, null);
  if (response.status !== 201 && response.status !== 202) {
    throw new ActRefusedError(
      `“${input.file.name}” was not uploaded`,
      response.status,
      await detailOf(response),
    );
  }
  const body = (await response.json()) as { artefact: unknown; warning?: unknown };
  return {
    artefact: SourceArtefactSchema.parse(body.artefact),
    warning: typeof body.warning === 'string' ? body.warning : null,
  };
}

export type AdvanceOutcome =
  | { kind: 'armed'; next: Next }
  | { kind: 'already_running'; next: Next }
  | { kind: 'nothing_to_run'; next: Next };

export async function advance(source: Source, id: string): Promise<AdvanceOutcome> {
  const response = await post(
    source,
    `/v1/assessments/${id}/advance`,
    json({}),
    'application/json',
  );
  if (response.status === 202 || response.status === 409) {
    const body = (await response.json()) as { code?: unknown; next?: unknown };
    const next = NextSchema.safeParse(body.next);
    if (!next.success) {
      throw new ReadFailedError(
        'the run',
        response.status,
        'The answer carried no `next` this console can read.',
      );
    }
    if (response.status === 202) return { kind: 'armed', next: next.data };
    if (body.code === 'already_running' || body.code === 'nothing_to_run') {
      return { kind: body.code, next: next.data };
    }
  }
  throw new ActRefusedError('the run was not started', response.status, await detailOf(response));
}

/** `POST /:id/domain-selection`. The guard's refusals — none chosen, one not enabled — arrive as the API's sentence. */
export async function confirmDomains(source: Source, id: string, domains: Domain[]): Promise<void> {
  const response = await post(
    source,
    `/v1/assessments/${id}/domain-selection`,
    json({ domains }),
    'application/json',
  );
  if (!response.ok) {
    throw new ActRefusedError(
      'the domains were not confirmed',
      response.status,
      await detailOf(response),
    );
  }
}

// ---------------------------------------------------------------------------
// The gate, and the report
// ---------------------------------------------------------------------------

export type ApproveOutcome =
  { kind: 'approved' } | { kind: 'refused'; rejection: TransitionRejection };

/** `POST /:id/approve`: through the gate, or the guard's refusal with the findings it names. */
export async function approve(source: Source, id: string): Promise<ApproveOutcome> {
  const response = await post(
    source,
    `/v1/assessments/${id}/approve`,
    json({}),
    'application/json',
  );
  if (response.status === 200) return { kind: 'approved' };
  if (response.status === 409) {
    const body = (await response.json()) as { rejection?: unknown; error?: unknown };
    const rejection = TransitionRejectionSchema.safeParse(body.rejection);
    if (rejection.success) return { kind: 'refused', rejection: rejection.data };
    // A 409 with `{error}` and no rejection is a route refusing before the
    // orchestrator was asked — the API's sentence, as with any other refusal.
    throw new ActRefusedError(
      'the assessment was not approved',
      response.status,
      typeof body.error === 'string'
        ? body.error
        : 'The answer carried no rejection this console can read.',
    );
  }
  throw new ActRefusedError(
    'the assessment was not approved',
    response.status,
    await detailOf(response),
  );
}

export interface GeneratedReport {
  report_id: string;
  size_bytes: number;
  source_finding_count: number;
}

/**
 * `POST /:id/report`: the report generated and stored, or the route's
 * refusal. A 409 here is a sentence about the assessment — not at
 * `approved_for_reporting`, or no finding released — and a 500 is the
 * product refusing to ship a document it found a defect in; both are the
 * API's words.
 */
export async function generateReport(source: Source, id: string): Promise<GeneratedReport> {
  const response = await post(source, `/v1/assessments/${id}/report`, json({}), 'application/json');
  if (response.status !== 200) {
    throw new ActRefusedError(
      'the report was not generated',
      response.status,
      await detailOf(response),
    );
  }
  const body = (await response.json()) as {
    report_id?: unknown;
    size_bytes?: unknown;
    source_finding_ids?: unknown;
  };
  if (typeof body.report_id !== 'string' || typeof body.size_bytes !== 'number') {
    throw new ReadFailedError('the report', 200, 'The response named no report.');
  }
  return {
    report_id: body.report_id,
    size_bytes: body.size_bytes,
    source_finding_count: Array.isArray(body.source_finding_ids)
      ? body.source_finding_ids.length
      : 0,
  };
}
